import React, { useEffect, useRef } from 'react';
import Map from '@arcgis/core/Map';
import MapView from '@arcgis/core/views/MapView';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import Graphic from '@arcgis/core/Graphic';
import Polygon from '@arcgis/core/geometry/Polygon';
import esriConfig from '@arcgis/core/config';
import * as reactiveUtils from '@arcgis/core/core/reactiveUtils';
import '@arcgis/core/assets/esri/themes/light/main.css';

// Renders a single zone layer (eligibility | priority). Each section of the
// Zones UI (Global Eligibility on the Dashboard Overview, Auction Focus
// Area) gets its own independent instance of this component rather than
// sharing one map with a tab switcher, so styling/popup behavior is scoped
// per `layerType`.
// `fallbackZones` (typically the tenant's Global Eligibility zones) is used
// ONLY to pick where the camera lands when `zones` is empty — it is never
// drawn. This gives the admin a sensible view of their operating area before
// a Focus Area layer has been uploaded for this auction.
const buildPolygons = (list) => {
    const polys = [];
    (list || []).forEach(zone => {
        if (!zone.geometry) return;
        let ring = zone.geometry.coordinates;
        if (zone.geometry.type === 'MultiPolygon') ring = zone.geometry.coordinates.flat(1);
        polys.push(new Polygon({ rings: ring, spatialReference: { wkid: 4326 } }));
    });
    return polys;
};

const ZoneManagerMap = ({ zones = [], layerType = 'priority', fallbackZones = [], onEditZone, onDeleteZone, loading = false }) => {
  const mapDiv = useRef(null);
  const viewRef = useRef(null);
  const layerRef = useRef(null);

  useEffect(() => {
    const apiKey = process.env.REACT_APP_ARCGIS_API_KEY_AGOL_API_SECRET;
    if (apiKey) esriConfig.apiKey = apiKey;
  }, []);

  // 1. INITIALIZE MAP
  useEffect(() => {
    if (!mapDiv.current) return;

    const map = new Map({ basemap: "gray-vector" });
    const layer = new GraphicsLayer({ title: layerType, opacity: layerType === 'eligibility' ? 0.4 : 0.6 });
    map.add(layer);
    layerRef.current = layer;

    const view = new MapView({
      container: mapDiv.current,
      map: map,
      center: [-0.90, 52.24],
      zoom: 9,
      popup: {
        dockEnabled: true,
        dockOptions: { buttonEnabled: false, breakpoint: false }
      }
    });
    viewRef.current = view;

    const actionHandle = reactiveUtils.on(
        () => view.popup, "trigger-action", (event) => {
            const selectedFeature = view.popup.selectedFeature;
            if (!selectedFeature) return;
            const actionId = event.action.id;
            const zoneData = selectedFeature.attributes;
            if (actionId === "edit-weight") {
                onEditZone(zoneData);
            } else if (actionId === "delete-zone") {
                onDeleteZone(zoneData._id);
                view.popup.close();
            }
        }
    );

    return () => {
        if (actionHandle) actionHandle.remove();
        if (viewRef.current) viewRef.current.destroy();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 2. UPDATE LAYER
  useEffect(() => {
    const layer = layerRef.current;
    const view = viewRef.current;
    if (!layer || !view) return;

    let selectedZoneId = null;
    if (view.popup.visible && view.popup.selectedFeature) {
        selectedZoneId = view.popup.selectedFeature.attributes?._id;
    }

    layer.removeAll();
    let newSelectedGraphic = null;

    zones.forEach(zone => {
        if (!zone.geometry) return;
        let ring = zone.geometry.coordinates;
        if (zone.geometry.type === 'MultiPolygon') ring = zone.geometry.coordinates.flat(1);

        let symbol, popupTemplate;

        if (layerType === 'eligibility') {
            symbol = { type: "simple-fill", color: [50, 50, 50, 0.1], outline: { color: [50, 50, 50], width: 1.5, style: "dash" } };
            popupTemplate = { title: zone.name || "Global Eligibility", content: "Base eligibility boundary." };
        } else {
            const weight = parseFloat(zone.weight);
            let color = [52, 152, 219, 0.4]; let outline = [52, 152, 219];
            if (weight >= 2.0) { color = [192, 57, 43, 0.5]; outline = [192, 57, 43]; }
            else if (weight >= 1.5) { color = [230, 126, 34, 0.5]; outline = [230, 126, 34]; }
            symbol = { type: "simple-fill", color: color, outline: { color: outline, width: 2 } };
            popupTemplate = {
                title: "{name}", content: "Weight: <b>x{weight}</b>",
                actions: [{ title: "Edit", id: "edit-weight", className: "esri-icon-edit" }, { title: "Delete", id: "delete-zone", className: "esri-icon-trash" }]
            };
        }

        const polygonGeometry = new Polygon({ rings: ring, spatialReference: { wkid: 4326 } });
        const graphic = new Graphic({
            geometry: polygonGeometry,
            symbol,
            attributes: { ...zone },
            popupTemplate
        });
        layer.add(graphic);
        if (zone._id === selectedZoneId) newSelectedGraphic = graphic;

        // Focus Area zones show their weight as an always-visible map label
        // (not just in the click popup) so admins can scan a layer with many
        // subzones at a glance.
        if (layerType !== 'eligibility') {
            layer.add(new Graphic({
                geometry: polygonGeometry.centroid,
                symbol: {
                    type: "text",
                    text: `Weight: x${zone.weight}`,
                    color: [15, 23, 42],
                    haloColor: [255, 255, 255],
                    haloSize: 1,
                    font: { size: 10, family: '"Inter", sans-serif', weight: "bold" }
                }
            }));
        }
    });

    if (newSelectedGraphic) {
        view.popup.open({ features: [newSelectedGraphic], updateLocationEnabled: false });
    }

    if (view && !loading) {
        const primaryTargets = buildPolygons(zones);
        const targets = primaryTargets.length > 0 ? primaryTargets : buildPolygons(fallbackZones);
        if (targets.length > 0) {
            // On a freshly-mounted view (e.g. opening the Zones tab for an
            // auction that already has an active layer), goTo() called
            // immediately after construction can race the view's own
            // initial center/zoom setup and get silently overridden.
            // view.when() waits for the view to finish initializing first.
            view.when(() => {
                view.goTo(targets, { padding: 40 }).catch(e => { if (e.name !== "AbortError") console.error(e); });
            }).catch(() => {});
        }
    }

  }, [zones, fallbackZones, layerType, loading]);

  return (
    <div style={{ position: 'relative', height: '500px', width: '100%' }}>
        {/* The Map */}
        <div ref={mapDiv} style={{ height: '100%', width: '100%', borderRadius: '8px', border:'1px solid #ccc' }} />

        {/* Loading Overlay */}
        {loading && (
            <div style={{
                position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                backgroundColor: 'rgba(255, 255, 255, 0.8)', zIndex: 999,
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                borderRadius: '8px', backdropFilter: 'blur(2px)'
            }}>
                <div style={{
                    width: '40px', height: '40px', border: '4px solid #f3f3f3',
                    borderTop: '4px solid #3498db', borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                }}></div>
                <p style={{marginTop:'15px', color:'#555', fontWeight:'bold'}}>Processing GeoJSON...</p>
                <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
            </div>
        )}
    </div>
  );
};

export default ZoneManagerMap;
